import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { isAdminUser, fetchWithAuth } from '../../../utils/api';
import { Navigate } from 'react-router-dom';
import IconWrapper from '../../ui/IconWrapper';
import { Heading } from '../../ui/heading';
import { Timestamp } from '../../ui/timestamp';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye, 
  FiClock, 
  FiCheckCircle, 
  FiXCircle, 
  FiPause,
  FiFileText,
  FiUsers
} from 'react-icons/fi';

interface ImportSession {
  id: string;
  session_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  status: 'pending' | 'analyzing' | 'processing' | 'completed' | 'failed' | 'cancelled';
  total_rows: number | null;
  total_batches: number | null;
  batch_size: number;
  processed_rows: number;
  successful_signals: number;
  failed_signals: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  progress_percent: number | null;
}

interface ImportHistoryResponse {
  total: number;
  limit: number;
  offset: number;
  sessions: ImportSession[];
}

interface Filters {
  status: string;
  organization_id: string;
  filename: string;
}

const AdminImportsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [imports, setImports] = useState<ImportSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    limit: 20
  });
  
  const [filters, setFilters] = useState<Filters>({
    status: '',
    organization_id: '',
    filename: ''
  });
  
  const [searchTerm, setSearchTerm] = useState('');

  const fetchImports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: (pagination.page * pagination.limit).toString()
      });
      
      if (filters.status) params.append('status', filters.status);
      if (filters.organization_id) params.append('organization_id', filters.organization_id);
      if (searchTerm) params.append('filename', searchTerm);
      
      const data: ImportHistoryResponse = await fetchWithAuth(`/v1/admin/imports?${params}`);
      setImports(data.sessions);
      setPagination(prev => ({ ...prev, total: data.total }));
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch imports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImports();
  }, [pagination.page, pagination.limit, filters, searchTerm]);

  // Only admin users can access this page
  if (!isAdminUser(user)) {
    return <Navigate to="/v4/records" replace />;
  }

  const getStatusIcon = (status: ImportSession['status']) => {
    switch (status) {
      case 'completed':
        return <IconWrapper icon={FiCheckCircle} className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <IconWrapper icon={FiXCircle} className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <IconWrapper icon={FiRefreshCw} className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'pending':
        return <IconWrapper icon={FiClock} className="w-4 h-4 text-yellow-500" />;
      case 'cancelled':
        return <IconWrapper icon={FiPause} className="w-4 h-4 text-gray-500" />;
      default:
        return <IconWrapper icon={FiClock} className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ImportSession['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 0 }));
    // fetchImports will be triggered by useEffect
  };

  const handleViewDetails = (sessionId: string) => {
    navigate(`/v3/admin/imports/${sessionId}`);
  };

  const handleStatusFilter = (status: string) => {
    setFilters(prev => ({ ...prev, status }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <Heading level={2} className="text-figma-2xl mb-2">Import History</Heading>
        <p className="text-figma-sm text-gray-600">
          View and manage all signal import sessions across all organizations
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <IconWrapper 
                icon={FiSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" 
              />
              <input
                type="text"
                placeholder="Search by filename..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-figma-blue focus:border-transparent"
              />
            </div>
          </form>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <IconWrapper icon={FiFilter} className="w-4 h-4 text-gray-500" />
            <select
              value={filters.status}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-figma-blue focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="analyzing">Analyzing</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={fetchImports}
            disabled={loading}
            className="px-4 py-2 bg-figma-blue text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
          >
            <IconWrapper 
              icon={FiRefreshCw} 
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <IconWrapper icon={FiXCircle} className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File & Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Signals
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <IconWrapper icon={FiRefreshCw} className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="text-gray-500">Loading imports...</span>
                    </div>
                  </td>
                </tr>
              ) : imports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <IconWrapper icon={FiFileText} className="w-8 h-8 text-gray-400" />
                      <span className="text-gray-500">No import sessions found</span>
                      {(searchTerm || filters.status) && (
                        <span className="text-sm text-gray-400">Try adjusting your search or filters</span>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                imports.map((importSession) => (
                  <tr key={importSession.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <IconWrapper icon={FiFileText} className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <div className="font-medium text-gray-900 mb-1">
                            {importSession.filename}
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(importSession.status)}
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(importSession.status)}`}>
                              {importSession.status.charAt(0).toUpperCase() + importSession.status.slice(1)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {formatFileSize(importSession.file_size)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {importSession.total_rows && (
                          <div className="text-sm text-gray-900">
                            {importSession.processed_rows} / {importSession.total_rows} rows
                          </div>
                        )}
                        {importSession.progress_percent !== null && (
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-figma-blue h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(importSession.progress_percent, 100)}%` }}
                            />
                          </div>
                        )}
                        {importSession.total_batches && (
                          <div className="text-xs text-gray-500">
                            {importSession.total_batches} batches
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <IconWrapper icon={FiCheckCircle} className="w-3 h-3 text-green-500" />
                          <span className="text-green-700">{importSession.successful_signals}</span>
                        </div>
                        {importSession.failed_signals > 0 && (
                          <div className="flex items-center gap-1 text-sm">
                            <IconWrapper icon={FiXCircle} className="w-3 h-3 text-red-500" />
                            <span className="text-red-700">{importSession.failed_signals}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 text-sm text-gray-500">
                        <div>
                          Created:{' '}
                          <Timestamp value={importSession.created_at} format="absolute" />
                        </div>
                        {importSession.started_at && (
                          <div>
                            Started:{' '}
                            <Timestamp value={importSession.started_at} format="absolute" />
                          </div>
                        )}
                        {importSession.completed_at && (
                          <div>
                            Completed:{' '}
                            <Timestamp value={importSession.completed_at} format="absolute" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewDetails(importSession.session_id)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-figma-blue hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        <IconWrapper icon={FiEye} className="w-4 h-4" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pagination.page * pagination.limit + 1} to{' '}
                {Math.min((pagination.page + 1) * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(0, prev.page - 1) }))}
                  disabled={pagination.page === 0}
                  className="px-3 py-1 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(totalPages - 1, prev.page + 1) }))}
                  disabled={pagination.page >= totalPages - 1}
                  className="px-3 py-1 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminImportsPage;